import { CONTENT_WIDTH, DATE_FORMATS } from "@/enums";
import { ISelectOption } from "@/interfaces";
import {Switch, DatePicker, Input, InputNumber, InputNumberProps, Select, UploadFile, UploadProps} from "antd";
import { useIntl } from "react-intl";
import * as Styled from "../styled";
import { InitiativeDocumentsUpload } from "./InitiativeDocumentsUpload";

interface IInitiativeFormFieldsProps {
  projectTypeOptions: ISelectOption[];
  productCategoryOptions: ISelectOption[];
  fileList: UploadFile[];
  initiativePublicId: string | undefined;
  placeholderId: string;
  productLine0Options: ISelectOption[];
  productLine1Options: ISelectOption[];
  productLine2Options: ISelectOption[];
  opportunityTypeOptions: ISelectOption[];
  engineeringComplexityOptions: ISelectOption[];
  commercialComplexityOptions: ISelectOption[];
  fitToServiceOptions: ISelectOption[];
  requirePromotionFields?: boolean;
  beforeUpload: UploadProps["beforeUpload"];
  onRemove: UploadProps["onRemove"];
  onChange: UploadProps["onChange"];
  onPreview: UploadProps["onPreview"];
}

export const InitiativeFormFields = ({
  projectTypeOptions,
  productCategoryOptions,
  fileList,
  initiativePublicId,
  placeholderId,
  productLine0Options,
  productLine1Options, 
  productLine2Options,
  opportunityTypeOptions,
  engineeringComplexityOptions,
  commercialComplexityOptions,
  fitToServiceOptions,
  requirePromotionFields = false,
  beforeUpload,
  onRemove,
  onChange,
  onPreview,
}: IInitiativeFormFieldsProps): React.JSX.Element => {
  const intl = useIntl();
  const promotionFieldRules = requirePromotionFields
    ? [
        {
          required: true,
          message: intl.formatMessage({
            id: "general.fieldRequiredMessage",
          }),
        },
      ]
    : undefined;

    const moneyFormatter: InputNumberProps<number>['formatter'] = (value) => {
        const [start, end] = `${value}`.split('.') || [];
        const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `$ ${end ? `${v}.${end}` : `${v}`}`;
    };

    return (
    <Styled.SectionRow justify="start">
      <Styled.SectionHeaderItem>
        {intl.formatMessage({ id: "initiative.requiredFields" })}
      </Styled.SectionHeaderItem>
      <Styled.FieldsColumn
        xxl={CONTENT_WIDTH.oneQuarter}
        xl={CONTENT_WIDTH.oneThird}
        lg={CONTENT_WIDTH.half}
        xs={CONTENT_WIDTH.max}
      >
        <Styled.FieldsCard
          size="small"
          title={intl.formatMessage({ id: "initiative.keyInformation" })}
          $required
        >
          <Styled.FormItem
            label={intl.formatMessage({ id: "initiative.opportunityName" })}
            name="name"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "general.fieldRequiredMessage",
                }),
              },
            ]}
          >
            <Input />
          </Styled.FormItem>
          <Styled.FormItem
            label={intl.formatMessage({ id: "projects.projectType" })}
            name="projectType"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "general.fieldRequiredMessage",
                }),
              },
            ]}
          >
            <Select options={projectTypeOptions} />
          </Styled.FormItem>
          <Styled.FormItem
            label={intl.formatMessage({ id: "projects.productCategory" })}
            name="productCategory"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "general.fieldRequiredMessage",
                }),
              },
            ]}
          >
            <Select options={productCategoryOptions} />
          </Styled.FormItem>
          <InitiativeDocumentsUpload
            fileList={fileList}
            initiativePublicId={initiativePublicId}
            placeholderId={placeholderId}
            beforeUpload={beforeUpload}
            onRemove={onRemove}
            onChange={onChange}
            onPreview={onPreview}
          />
          <Styled.FormItem
            label={intl.formatMessage({ id: "initiative.estimatedTargetLaunchDate" })}
            name="estimatedTargetLaunchDate"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "general.fieldRequiredMessage",
                }),
              },
            ]}
          >
            <Styled.EstimatedLaunchDatePicker
              format={DATE_FORMATS.fourDigitYear}
              placeholder={DATE_FORMATS.fourDigitYear}
              data-test-id="initiative-estimated-launch-date-date-picker"
            />
          </Styled.FormItem>
        </Styled.FieldsCard>
      </Styled.FieldsColumn>
        <Styled.FieldsColumn>
            <Styled.FieldsCard
                size="small"
                title={intl.formatMessage({ id: "initiative.promotionFields" })}
                $required={requirePromotionFields}
            >
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.productLine0" })}
                    name="productLine0"
                    rules={promotionFieldRules}
                >
                    <Select options={productLine0Options} />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.productLine1" })}
                    name="productLine1"
                    rules={promotionFieldRules}
                >
                    <Select options={productLine1Options} />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.productLine2" })}
                    name="productLine2"
                    rules={promotionFieldRules}
                >
                    <Select options={productLine2Options} />
                </Styled.FormItem>

                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.sbuPitchedDate" })}
                    name="sbuPitchedDate"
                    rules={promotionFieldRules}
                >
                    <DatePicker
                        format={DATE_FORMATS.fourDigitYear}
                        placeholder={DATE_FORMATS.fourDigitYear}
                        data-test-id="initiative-sbu-pitched-date-date-picker"
                    />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.opportunityType" })}
                    name="opportunityType"
                    rules={promotionFieldRules}
                >
                    <Select options={opportunityTypeOptions} />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.digital" })}
                    name="digital"
                    valuePropName="checked"
                >
                  <Switch
                    data-test-id="initiative-digital-switch"
                  />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.yearOneNetSalesNorthAmerica"})}
                    name="yearOneNetSalesNorthAmerica"
                    rules={promotionFieldRules}
                >
                    <InputNumber<number>
                        formatter={moneyFormatter}
                        parser={(value) => value?.replace(/\$\s?|(,*)/g, '') as unknown as number}
                    />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.grossMarginPercentage" })}
                    name="grossMarginPercentage"
                    rules={promotionFieldRules}
                >
                    <InputNumber<number>
                        min={0}
                        max={100}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value?.replace('%', '') as unknown as number}
                    />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.engineeringComplexity" })}
                    name="engineeringComplexity"
                    rules={promotionFieldRules}
                >
                    <Select options={engineeringComplexityOptions} />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.commercialComplexity" })}
                    name="commercialComplexity"
                    rules={promotionFieldRules}
                >
                    <Select options={commercialComplexityOptions} />
                </Styled.FormItem>
                <Styled.FormItem
                    label={intl.formatMessage({ id: "initiative.fitToService" })}
                    name="fitToService"
                    rules={promotionFieldRules}
                >
                    <Select options={fitToServiceOptions} />
                </Styled.FormItem>
            </Styled.FieldsCard>
        </Styled.FieldsColumn>
    </Styled.SectionRow>
  );
};

export default InitiativeFormFields;
